import React, {useMemo, useState} from 'react'
import moment from 'moment'
import {ToastContainer} from 'react-toastify'
import {pdf} from '@react-pdf/renderer'
import {KTIcon} from '../../../_websoft/helpers'
import {getIsporuceniOtpremljeniTbPaketi, otpremiTbPakete} from '../../api/thermoboardNalozi'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'
import OtpremnicaPdf from './OtpremnicaPdf'

type Uposlenik = {
  UposlenikImePrezime?: string
}

type TipPanela = {
  Naziv?: string
  naziv?: string
}

type ThermoBoardPaket = {
  Id: number
  Sirina?: string | number
  Duzina?: string | number
  Komada?: string | number
  DatumIsporuke?: string
  PaketOtpremljen?: number
  BrojNaloga?: string | number
  BrojFmkNaloga?: string | number
  KupacIdFMK?: string | number
  KupacNaziv?: string
  Komercijalista?: string
  tip_panela?: TipPanela | null
  proizveo_uposlenik?: Uposlenik | null
  isporucio_uposlenik?: Uposlenik | null
}

const getResponseRows = (responseData: any): ThermoBoardPaket[] => {
  const possibleRows = [
    responseData,
    responseData?.data,
    responseData?.data?.data,
    responseData?.data?.data?.data,
  ]

  const rows = possibleRows.find((value) => Array.isArray(value))

  return rows ?? []
}

const formatDate = (value?: string) => {
  if (!value) {
    return '-'
  }

  return moment(value).isValid() ? moment(value).format('DD-MM-YYYY') : value
}

const getTipPanelaLabel = (paket: ThermoBoardPaket) => {
  return paket.tip_panela?.Naziv || paket.tip_panela?.naziv || '-'
}

const getBrojNaloga = (paket: ThermoBoardPaket) => {
  return paket.BrojFmkNaloga ?? paket.BrojNaloga ?? '-'
}

const getKupacKey = (paket: ThermoBoardPaket) => {
  return paket.KupacIdFMK?.toString() || paket.KupacNaziv || ''
}

const parseDimensionValue = (value?: string | number) => {
  if (value === undefined || value === null || value === '') {
    return 0
  }

  return Number(value.toString().replace(',', '.')) || 0
}

const calculateSquareMeters = (paket: ThermoBoardPaket) => {
  const duzina = parseDimensionValue(paket.Duzina)
  const sirina = parseDimensionValue(paket.Sirina)
  const komada = parseDimensionValue(paket.Komada)

  return (duzina / 1000) * (sirina / 1000) * komada
}

const formatSquareMeters = (paket: ThermoBoardPaket) => {
  const squareMeters = calculateSquareMeters(paket)

  return squareMeters > 0 ? squareMeters.toFixed(2) : '-'
}

const isPaketOtpremljen = (paket: ThermoBoardPaket) => Number(paket.PaketOtpremljen) === 1

const OtpremniceDetails: React.FC = () => {
  const [datum, setDatum] = useState(moment().format('YYYY-MM-DD'))
  const [paketi, setPaketi] = useState<ThermoBoardPaket[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const selectedPaketi = useMemo(
    () => paketi.filter((paket) => selectedIds.includes(paket.Id)),
    [paketi, selectedIds]
  )

  const selectedSquareMeters = useMemo(
    () => selectedPaketi.reduce((total, paket) => total + calculateSquareMeters(paket), 0),
    [selectedPaketi]
  )

  const selectedKupacKey = useMemo(() => {
    const selectedPaket = selectedPaketi[0]

    return selectedPaket ? getKupacKey(selectedPaket) : ''
  }, [selectedPaketi])

  const selectedKupacNaziv = useMemo(() => {
    const selectedPaket = selectedPaketi[0]

    return selectedPaket?.KupacNaziv || ''
  }, [selectedPaketi])

  const selectableIds = useMemo(
    () =>
      paketi
        .filter((paket) => {
          if (isPaketOtpremljen(paket)) {
            return false
          }

          return !selectedKupacKey || getKupacKey(paket) === selectedKupacKey
        })
        .map((paket) => paket.Id),
    [paketi, selectedKupacKey]
  )

  const allSelectableChecked =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id))

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!datum) {
      notifyToast(false, 'Odaberite datum.')
      return
    }

    setLoading(true)
    setHasSearched(true)
    setSelectedIds([])

    try {
      const response = await getIsporuceniOtpremljeniTbPaketi({datum})
      setPaketi(getResponseRows(response))
    } catch (error: any) {
      notifyToast(
        false,
        error?.response?.data?.message || error?.message || 'Greska pri ucitavanju paketa.'
      )
    } finally {
      setLoading(false)
    }
  }

  const toggleSelectedId = (id: number) => {
    const paket = paketi.find((item) => item.Id === id)

    if (!paket) {
      return
    }

    if (selectedKupacKey && getKupacKey(paket) !== selectedKupacKey && !selectedIds.includes(id)) {
      notifyToast(false, 'Mozete odabrati pakete samo za istog kupca.')
      return
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    )
  }

  const toggleAllSelectable = () => {
    if (allSelectableChecked) {
      setSelectedIds([])
      return
    }

    if (selectedKupacKey) {
      setSelectedIds(selectableIds)
      return
    }

    const firstSelectablePaket = paketi.find((paket) => !isPaketOtpremljen(paket))
    const firstKupacKey = firstSelectablePaket ? getKupacKey(firstSelectablePaket) : ''

    setSelectedIds(
      paketi
        .filter((paket) => !isPaketOtpremljen(paket) && getKupacKey(paket) === firstKupacKey)
        .map((paket) => paket.Id)
    )
  }

  const kreirajOtpremnicu = async () => {
    if (selectedPaketi.length === 0) {
      notifyToast(false, 'Odaberite pakete za otpremnicu.')
      return
    }

    setPdfLoading(true)

    try {
      const response = await otpremiTbPakete({stavkaIds: selectedIds})

      if (response?.success === false || response?.error || response instanceof Error) {
        notifyToast(false, response?.message || 'Greska pri otpremi paketa.')
        return
      }

      const blob = await pdf(
        <OtpremnicaPdf
          paketi={selectedPaketi}
          datum={moment().format('DD-MM-YYYY')}
          ukupnoKvadrata={selectedSquareMeters}
        />
      ).toBlob()
      const url = URL.createObjectURL(blob)

      window.open(url, '_blank', 'noopener,noreferrer')?.focus()
      notifyToast(true, response?.message || 'Otpremnica je kreirana.')
      setPaketi((prev) =>
        prev.map((paket) =>
          selectedIds.includes(paket.Id)
            ? {
                ...paket,
                PaketOtpremljen: 1,
              }
            : paket
        )
      )
      setSelectedIds([])
    } catch (error: any) {
      notifyToast(false, error?.message || 'Greska pri kreiranju otpremnice.')
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div className='card shadow-sm'>
      <ToastContainer />

      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column align-items-start m-0'>
          <div className='d-flex align-items-center gap-3'>
            <div className='symbol symbol-45px'>
              <div className='symbol-label bg-light-primary'>
                <KTIcon iconName='truck' className='fs-2 text-primary' />
              </div>
            </div>
            <div>
              <h1 className='fs-3 fw-bold text-gray-900 mb-1'>ThermoBoard otpremnice</h1>
              <div className='text-muted fw-semibold fs-7'>
                Pregled isporucenih paketa po datumu i statusu otpreme.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body border-top p-6 p-md-8'>
        <form onSubmit={handleSubmit} className='rounded bg-light p-5 mb-7'>
          <div className='row g-4 align-items-end'>
            <div className='col-12 col-md-5 col-xl-3'>
              <label className='form-label required fw-bold fs-6'>Datum isporuke</label>
              <input
                type='date'
                value={datum}
                onChange={(event) => setDatum(event.target.value)}
                className='form-control form-control-lg form-control-solid'
                disabled={loading}
              />
            </div>

            <div className='col-12 col-md-auto'>
              <button
                type='submit'
                className='btn btn-primary fw-bold px-8 py-4'
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Ucitavanje...
                  </>
                ) : (
                  'Potvrdi'
                )}
              </button>
            </div>

            <div className='col-12 col-md'>
              <div className='d-flex flex-column flex-md-row justify-content-md-end gap-3'>
                <div className='badge badge-light-primary fs-7 p-3'>
                  Paketa: <span className='fw-bold ms-1'>{paketi.length}</span>
                </div>
                <div className='badge badge-light-success fs-7 p-3'>
                  Odabrano: <span className='fw-bold ms-1'>{selectedIds.length}</span>
                </div>
                <div className='badge badge-light-warning fs-7 p-3'>
                  Kvadratura:{' '}
                  <span className='fw-bold ms-1'>{selectedSquareMeters.toFixed(2)} m2</span>
                </div>
                {selectedKupacNaziv && (
                  <div className='badge badge-light-info fs-7 p-3'>
                    Kupac: <span className='fw-bold ms-1'>{selectedKupacNaziv}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className='d-flex justify-content-end mb-4'>
          <button
            type='button'
            className='btn btn-success fw-bold'
            disabled={selectedPaketi.length === 0 || pdfLoading}
            onClick={kreirajOtpremnicu}
          >
            {pdfLoading ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' />
                Kreiranje...
              </>
            ) : (
              'Kreiraj Otpremnicu'
            )}
          </button>
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <div className='table-responsive border rounded'>
            <table className='table table-row-bordered table-row-gray-200 align-middle gy-4 mb-0'>
              <thead>
                <tr className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark'>
                  <th className='text-white ps-4 w-50px'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      checked={allSelectableChecked}
                      onChange={toggleAllSelectable}
                      disabled={selectableIds.length === 0}
                    />
                  </th>
                  <th className='text-white'>Broj naloga</th>
                  <th className='text-white'>Vrsta</th>
                  <th className='text-white'>Dimenzije</th>
                  <th className='text-white'>Square meter</th>
                  <th className='text-white'>Kupac</th>
                  <th className='text-white'>Komercijalista</th>
                  <th className='text-white'>Proizveo uposlenik</th>
                  <th className='text-white'>Isporucio</th>
                  <th className='text-white'>Datum isporuke</th>
                  <th className='text-white'>Paket otpremljen</th>
                </tr>
              </thead>
              <tbody className='text-gray-700'>
                {paketi.length > 0 ? (
                  paketi.map((paket) => {
                    const otpremljen = isPaketOtpremljen(paket)
                    const isDifferentKupac =
                      Boolean(selectedKupacKey) && getKupacKey(paket) !== selectedKupacKey

                    return (
                      <tr key={paket.Id} className={otpremljen ? 'bg-light' : ''}>
                        <td className='ps-4'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            value={paket.Id}
                            checked={selectedIds.includes(paket.Id)}
                            disabled={otpremljen || isDifferentKupac}
                            onChange={() => toggleSelectedId(paket.Id)}
                          />
                        </td>
                        <td>{getBrojNaloga(paket)}</td>
                        <td className='fw-bold'>{getTipPanelaLabel(paket)}</td>
                        <td>{paket.Duzina ?? '-'} X {paket.Sirina ?? '-'} X {paket.Komada ?? '-'}</td>
                        <td>{formatSquareMeters(paket)}</td>
                        <td>{paket.KupacNaziv || '-'}</td>
                        <td>{paket.Komercijalista || '-'}</td>
                        <td>{paket.proizveo_uposlenik?.UposlenikImePrezime || '-'}</td>
                        <td>{paket.isporucio_uposlenik?.UposlenikImePrezime || '-'}</td>
                        <td>{formatDate(paket.DatumIsporuke)}</td>
                        <td>
                          {otpremljen ? (
                            <span className='badge badge-light-success fw-bold'>
                              <KTIcon iconName='check' className='fs-3 me-1' />
                              Da
                            </span>
                          ) : (
                            <span className='badge badge-light-danger fw-bold'>
                              <KTIcon iconName='cross' className='fs-3 me-1' />
                              Ne
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={11}>
                      <div className='d-flex justify-content-center py-7 text-muted fw-semibold'>
                        {hasSearched
                          ? 'Nema isporucenih paketa za odabrani datum.'
                          : 'Odaberite datum i kliknite Potvrdi za prikaz paketa.'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default OtpremniceDetails
